import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { formatNumber } from '../../helpers/numberUtils';
import { formatBerlin, renderInt } from './shared';

export interface DedupHealthDay {
  send_date: string;
  campaigns_checked: number;
  pairs_checked: number;
  total_overlap: number;
  assigned_violations: number;
  prediction_misses: number;
  unassigned_residue: number;
  /** Überschneidungen mit Beteiligung einer Reaktivierungs-Sonde. Erst seit
   *  2026-08-19 gemessen — ältere Zeilen stehen auf 0, weil die Klasse
   *  weggefiltert wurde, nicht weil sie leer war. */
  reactivation_violations: number;
  per_pair: Array<{ a: string; b: string; overlap: number }>;
  /** Parsed samples_json — `incomplete` lists campaigns whose sent-to list
   *  was not ready at verification time (their pairs went unchecked). */
  samples: { incomplete?: string[] } | null;
  duration_ms: number | null;
}

export interface CronStatusRow {
  scheduler_name: string;
  last_status: string;
  last_started_at: string | null;
  last_completed_at: string | null;
  last_row_count: number | null;
  last_error_message: string | null;
  consecutive_failures: number;
}

export interface DedupHealthResponse {
  days: number;
  series: DedupHealthDay[];
  latest: DedupHealthDay | null;
  crons: Record<string, CronStatusRow | undefined>;
}

interface Props {
  data: DedupHealthResponse | null;
  loading: boolean;
}

const CRON_LABELS: Record<string, string> = {
  sam_dedup_morning_resync: '05:20 Resync',
  sam_dedup_verification: '10:30 Verifikation',
};

const cronChipColor = (status: string | undefined): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'success') return 'success';
  if (status === 'warn') return 'warning';
  if (status === 'failure') return 'error';
  return 'default';
};

const tooltipBox = (label: string, value: string) => (
  <div
    style={{
      background: '#fff',
      color: '#111',
      padding: '6px 10px',
      borderRadius: 4,
      boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
      fontSize: 13,
    }}
  >
    <strong>{label}</strong>: {value}
  </div>
);

const UnifiedDedupHealthBlock: React.FC<Props> = ({ data, loading }) => {
  const barData = useMemo(
    () =>
      (data?.series ?? []).map((d) => ({
        date: d.send_date.slice(5), // MM-DD
        Verletzungen: Number(d.assigned_violations) || 0,
        Reaktivierung: Number(d.reactivation_violations) || 0,
        'Prediction-Miss': Number(d.prediction_misses) || 0,
        Residuum: Number(d.unassigned_residue) || 0,
      })),
    [data],
  );

  if (loading && !data) return <CircularProgress sx={{ mb: 2 }} />;
  if (!data) return null;

  const latestPairs = (data.latest?.per_pair ?? [])
    .slice()
    .sort((a, b) => b.overlap - a.overlap)
    .slice(0, 10);

  return (
    <Paper sx={{ backgroundColor: '#ffffff', p: 2, mb: 3, height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Dedup-Health (30 Tage)
      </Typography>
      <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 1 }}>
        Nächtliche Sent-To-Verifikation: Verletzungen = Suppression-Segment hat nicht gehalten
        (alarmiert); Reaktivierung = Sonde traf eine weitere Werbemail desselben Tages (alarmiert,
        erst ab 19.08. gemessen — davor weggefiltert, ältere 0 sind kein Freispruch);
        Prediction-Miss + Residuum = akzeptierte Klassen. Lücken = Tage ohne Verifikations-Lauf.
      </Typography>

      <Box display="flex" gap={1} sx={{ mb: 1 }} flexWrap="wrap">
        {Object.entries(CRON_LABELS).map(([name, label]) => {
          const cron = data.crons[name];
          return (
            <Chip
              key={name}
              size="small"
              label={`${label}: ${cron?.last_status ?? 'nie gelaufen'}${
                cron?.last_started_at ? ` · ${formatBerlin(cron.last_started_at)}` : ''
              }`}
              color={cronChipColor(cron?.last_status)}
              variant="outlined"
            />
          );
        })}
      </Box>

      {barData.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Noch keine Verifikations-Ergebnisse — der 10:30-Cron füllt die Historie täglich.
        </Typography>
      ) : (
        <Box height={220}>
          <ResponsiveBar
            data={barData}
            keys={['Verletzungen', 'Reaktivierung', 'Prediction-Miss', 'Residuum']}
            indexBy="date"
            groupMode="stacked"
            margin={{ top: 10, right: 20, bottom: 45, left: 45 }}
            padding={0.3}
            colors={['#d32f2f', '#8e24aa', '#ffb74d', '#90caf9']}
            theme={{
              text: { fill: '#111' },
              axis: { ticks: { text: { fill: '#111' } } },
              tooltip: { container: { background: '#ffffff', color: '#111' } },
            }}
            axisBottom={{ tickRotation: -45 }}
            axisLeft={{ format: (v) => formatNumber(v as number, 0) }}
            labelSkipHeight={14}
            valueFormat={(v) => formatNumber(v as number, 0)}
            tooltip={({ id, value, indexValue }) =>
              tooltipBox(`${indexValue} · ${id}`, formatNumber(value as number, 0))
            }
          />
        </Box>
      )}

      {data.latest && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: '#555' }}>
            Letzter Lauf ({data.latest.send_date}): {renderInt(data.latest.campaigns_checked)}{' '}
            Kampagnen, {renderInt(data.latest.pairs_checked)} Paare,{' '}
            {renderInt(data.latest.total_overlap)} Overlap gesamt.
          </Typography>
          {(data.latest.samples?.incomplete?.length ?? 0) > 0 && (
            <Chip
              size="small"
              color="warning"
              sx={{ ml: 1 }}
              label={`unvollständig (${data.latest.samples?.incomplete?.length}) — Paare ungeprüft`}
            />
          )}
          {latestPairs.length > 0 && (
            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Paar', 'Overlap'].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          backgroundColor: '#e0e0e0 !important',
                          color: 'black',
                          fontWeight: 'bold',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {latestPairs.map((p) => (
                    <TableRow key={`${p.a}|${p.b}`}>
                      <TableCell>
                        {p.a} ∩ {p.b}
                      </TableCell>
                      <TableCell align="right">{renderInt(p.overlap)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default UnifiedDedupHealthBlock;
